import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { CONFIG } from '../config';

const { FiSend, FiCheckCircle, FiAlertCircle, FiPhone, FiMail } = FiIcons;

const QuoteForm = () => {
  const [step, setStep] = useState('form');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', business: '',
    industry: '', location: '', website: '', revenue: '',
    goal: '', demo: '', notes: ''
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Required";
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Valid email required";
    if (!formData.phone) newErrors.phone = "Required";
    if (!formData.business) newErrors.business = "Required";
    if (!formData.industry) newErrors.industry = "Required";
    if (!formData.location) newErrors.location = "Required";
    if (!formData.revenue) newErrors.revenue = "Required";
    if (!formData.goal) newErrors.goal = "Required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setStep('loading');
    try {
      const res = await fetch('/.netlify/functions/submit-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) throw new Error('Submission failed');
      setStep('success');
    } catch (err) {
      console.error(err);
      setStep('error');
    }
  };

  return (
    <div id="quote" className="max-w-4xl mx-auto">
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="glass-strong rounded-[3rem] overflow-hidden border border-white/10"
      >
        <div className="grid lg:grid-cols-5">
          <div className="lg:col-span-2 bg-blue-600 p-12 md:p-16 flex flex-col justify-between text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-800" />
            <div className="absolute top-[-10%] right-[-10%] w-[80%] h-[80%] rounded-full bg-white/10 blur-[80px]" />
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold font-display mb-6 leading-tight">Request a Professional Quote</h2>
              <p className="text-blue-100 text-sm leading-relaxed mb-12">
                Tell us about your practice and the authority goals you want to achieve. We'll provide a fixed-scope proposal within 24 hours.
              </p>
              
              <div className="space-y-8">
                <div className="flex items-center gap-5">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center border border-white/20">
                    <SafeIcon icon={FiPhone} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-blue-200 font-black mb-1">Direct Line</p>
                    <p className="font-bold">{CONFIG.SUPPORT_PHONE}</p>
                  </div>
                </div>
                <div className="flex items-center gap-5">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center border border-white/20">
                    <SafeIcon icon={FiMail} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-blue-200 font-black mb-1">Expert Support</p>
                    <p className="font-bold">{CONFIG.SUPPORT_EMAIL}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative z-10 pt-12">
              <p className="text-[9px] uppercase tracking-[0.4em] font-black text-blue-300">
                BPA SOLUTIONS AUSTRALIA
              </p>
            </div>
          </div>

          <div className="lg:col-span-3 p-12 md:p-16 bg-[#0a0c10]">
            {step === 'form' && (
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2 space-y-1">
                  <label className="premium-label">Full Name *</label>
                  <input className="premium-input" placeholder="Your name" onChange={e => setFormData({...formData, name: e.target.value})} />
                  {errors.name && <p className="text-red-500 text-[10px] font-bold ml-1">{errors.name}</p>}
                </div>
                
                <div className="space-y-1">
                  <label className="premium-label">Work Email *</label>
                  <input type="email" className="premium-input" placeholder="email@firm.au" onChange={e => setFormData({...formData, email: e.target.value})} />
                  {errors.email && <p className="text-red-500 text-[10px] font-bold ml-1">{errors.email}</p>}
                </div>
                
                <div className="space-y-1">
                  <label className="premium-label">Phone *</label>
                  <input className="premium-input" placeholder="0400 000 000" onChange={e => setFormData({...formData, phone: e.target.value})} />
                  {errors.phone && <p className="text-red-500 text-[10px] font-bold ml-1">{errors.phone}</p>}
                </div>

                <div className="md:col-span-2 space-y-1">
                  <label className="premium-label">Business Name *</label>
                  <input className="premium-input" placeholder="Practice or Firm Name" onChange={e => setFormData({...formData, business: e.target.value})} />
                </div>

                <div className="space-y-1">
                  <label className="premium-label">Industry *</label>
                  <select className="premium-input appearance-none" onChange={e => setFormData({...formData, industry: e.target.value})}>
                    <option value="">Select Industry</option>
                    {CONFIG.INDUSTRIES.map(m => <option key={m} value={m} className="bg-[#080a0d]">{m}</option>)}
                  </select>
                </div>
                
                <div className="space-y-1">
                  <label className="premium-label">Location *</label>
                  <input className="premium-input" placeholder="e.g. Sydney, NSW" onChange={e => setFormData({...formData, location: e.target.value})} />
                </div>

                
                
                <div className="space-y-1">
                  <label className="premium-label">Primary Goal *</label>
                  <select className="premium-input appearance-none" onChange={e => setFormData({...formData, goal: e.target.value})}>
                    <option value="">Select Goal</option>
                    {CONFIG.PRIMARY_GOALS.map(g => <option key={g} value={g} className="bg-[#080a0d]">{g}</option>)}
                  </select>
                </div>

                <div className="md:col-span-2 space-y-1">
                  <label className="premium-label">Preferred Demo Framework</label>
                  <select className="premium-input appearance-none" onChange={e => setFormData({...formData, demo: e.target.value})}>
                    <option value="">Select Demo of Interest</option>
                    {CONFIG.DEMO_SITES.map(d => <option key={d.id} value={d.label} className="bg-[#080a0d]">{d.label}</option>)}
                  </select>
                </div>

                <div className="md:col-span-2 pt-6">
                  <button type="submit" className="btn-premium-primary w-full group py-6">
                    Request Strategy Quote
                    <SafeIcon icon={FiSend} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <p className="mt-6 text-center text-[10px] uppercase tracking-[0.3em] text-slate-500 font-black">
                    Secure Transmission &bull; Strategy Alignment Included
                  </p>
                </div>
              </form>
            )}

            {step === 'loading' && (
              <div className="py-24 text-center">
                <div className="animate-spin w-16 h-16 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-10" />
                <h3 className="text-2xl font-bold text-white mb-4 font-display">Securing Transmission</h3>
                <p className="text-slate-400 text-sm">Validating authority credentials...</p>
              </div>
            )}

            {step === 'error' && (
              <div className="py-24 text-center">
                <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-10 border border-red-500/20">
                  <SafeIcon icon={FiAlertCircle} className="text-4xl text-red-500" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 font-display">Submission Error</h3>
                <p className="text-slate-400 text-sm mb-10">We couldn't process your request. Please try again or contact us directly.</p>
                <button onClick={() => setStep('form')} className="btn-premium-secondary w-full">Try Again</button>
              </div>
            )}

            {step === 'success' && (
              <div className="py-16 text-center">
                <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-10 border border-green-500/20">
                  <SafeIcon icon={FiCheckCircle} className="text-4xl text-green-500" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-6 font-display">Request Received</h3>
                <p className="text-slate-400 text-sm mb-12 leading-relaxed font-medium">
                  Thank you for your interest. We will review your practice positioning and contact you via phone or email within 24 hours to discuss your authority page and arrange a consultation.
                </p>
                <div className="flex flex-col gap-4">
                  <a href={`tel:${CONFIG.SUPPORT_PHONE}`} className="text-blue-400 font-black text-[11px] uppercase tracking-widest">{CONFIG.SUPPORT_PHONE}</a>
                  <a href={`mailto:${CONFIG.SUPPORT_EMAIL}`} className="text-blue-400 font-black text-[11px] uppercase tracking-widest">{CONFIG.SUPPORT_EMAIL}</a>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default QuoteForm;